import { supabase } from './supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('USERNAMEVALIDATION');

const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;
const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;

// Minimal banned words list for V1 (lowercase)
const BANNED_WORDS = [
  'fuck', 'shit', 'bitch', 'cunt', 'dick', 'porn', 'sex'
];
const BANNED_RE = new RegExp(`(${BANNED_WORDS.join('|')})`);

export interface DisplayNameValidationResult {
  isValid: boolean;
  error?: string;
}

export interface UsernameValidationResult {
  isValid: boolean;
  error?: string;
}

export function validateUsernameFormat(username: string): UsernameValidationResult {
  const trimmed = username.trim();

  if (!trimmed) {
    return { isValid: false, error: 'Username is required' };
  }

  if (trimmed.length < USERNAME_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Username must be at least ${USERNAME_MIN_LENGTH} characters`,
    };
  }

  if (trimmed.length > USERNAME_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Username must be ${USERNAME_MAX_LENGTH} characters or less`,
    };
  }

  if (!USERNAME_REGEX.test(trimmed)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, and underscores',
    };
  }

  // Basic profanity check (do not echo the matched word back to UI)
  const lowered = trimmed.toLowerCase();
  if (BANNED_RE.test(lowered)) {
    return { isValid: false, error: 'Username not allowed.' };
  }

  return { isValid: true };
}

export function validateDisplayNameFormat(displayName: string): DisplayNameValidationResult {
  const trimmed = displayName.trim();
  if (trimmed.length < 2) return { isValid: false, error: 'Display name not allowed.' };
  if (trimmed.length > 30) return { isValid: false, error: 'Display name not allowed.' };

  // Allow letters, numbers, spaces, underscores, periods and hyphens
  const RE = /^[A-Za-z0-9 _.\-]+$/;
  if (!RE.test(trimmed)) return { isValid: false, error: 'Display name not allowed.' };

  const lowered = trimmed.toLowerCase();
  if (BANNED_RE.test(lowered)) return { isValid: false, error: 'Display name not allowed.' };

  return { isValid: true };
}

export async function checkUsernameAvailability(
  username: string,
  excludeUserId?: string,
  retryCount: number = 3
): Promise<UsernameValidationResult> {
  const normalized = username.trim().toLowerCase();

  for (let attempt = 1; attempt <= retryCount; attempt++) {
    let query = supabase
      .from('profiles')
      .select('handle')
      .eq('handle', normalized);

    if (excludeUserId) {
      query = query.neq('id', excludeUserId);
    }

    const { data, error } = await query.maybeSingle();

    if (!error) {
      if (data) {
        return { isValid: false, error: 'Username already taken' };
      }
      return { isValid: true };
    }

    log.error(`[USERNAME] Availability check attempt ${attempt}/${retryCount} failed:`, error);

    if (attempt < retryCount) {
      await new Promise(resolve => setTimeout(resolve, 500 * attempt));
    } else {
      return {
        isValid: false,
        error: 'Unable to verify username availability. Please check your connection and try again.',
      };
    }
  }

  return {
    isValid: false,
    error: 'Unable to verify username availability. Please try again.',
  };
}

export async function validateUsername(
  username: string,
  excludeUserId?: string
): Promise<UsernameValidationResult> {
  const formatValidation = validateUsernameFormat(username);
  if (!formatValidation.isValid) {
    return formatValidation;
  }

  return await checkUsernameAvailability(username, excludeUserId);
}

export async function verifyUsernameSet(
  userId: string,
  expectedUsername: string,
  retryCount: number = 3
): Promise<UsernameValidationResult> {
  const normalizedExpected = expectedUsername.trim().toLowerCase();

  for (let attempt = 1; attempt <= retryCount; attempt++) {
    const { data, error } = await supabase
      .from('profiles')
      .select('handle')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      log.error(`[USERNAME] Verification attempt ${attempt}/${retryCount} failed:`, error);
      if (attempt < retryCount) {
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        continue;
      }
      return {
        isValid: false,
        error: 'Unable to verify profile creation. Please try again.',
      };
    }

    if (!data) {
      log.error(`[USERNAME] Profile not found on attempt ${attempt}/${retryCount}`);
      if (attempt < retryCount) {
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        continue;
      }
      return {
        isValid: false,
        error: 'Profile was not created properly. Please try again.',
      };
    }

    const actual = (data as any)?.handle ?? null;
    if (!actual || actual !== normalizedExpected) {
      log.error('[USERNAME] Username mismatch:', {
        expected: normalizedExpected,
        actual,
      });
      return {
        isValid: false,
        error: 'Username was not saved correctly. Please try again.',
      };
    }

    return { isValid: true };
  }

  return {
    isValid: false,
    error: 'Unable to verify profile creation. Please try again.',
  };
}
